import ButtonHouston from '@eduzz/houston-ui/Button';
import { ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import Alert from 'components/Alert';
import PermissionHide from 'components/PermissionHide';
import { saveAs } from 'file-saver';
import { EXPORT_MAX_LINES_SIZE, EXPORT_MAX_LINES_TITLE, EXPORT_MAX_LIZES_MESSAGE } from 'helpers/constants';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import { BackgroundExportModal } from 'v2/components/Shared/BackgroundExportModal';
import salesService from 'v2/services/sales/general';

import ModalInfo from './modalInfo';

const useStyle = makeStyles((theme) => ({
  popper: {
    zIndex: 999,
  },
  exportBtn: {
    padding: `0 ${theme.spacing(2)}px`,
    outline: 0,
    color: '#37474F',
  },
}));

export interface IExportProps {}

const Export = () => {
  const classes = useStyle({});
  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalInfo, setModalInfo] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [exportType, setExportType] = useState<boolean>(false); //SIMPLE = false, DETAILED = true
  const [totalItems] = useObservable(() => salesService.getTotalItems(), []);

  const [exportSimple] = useCallbackObservable(() => {
    setOpen(false);
    setLoading(true);
    return salesService.simpleExport().pipe(
      tap(
        (data) => {
          try {
            const dateExport = moment(new Date()).format('DD-MM-YYYY');
            let fileExport = new Blob([data as any], { type: 'text/csv; charset=utf-8' });
            saveAs(fileExport, `Simples - minhas-vendas-${dateExport}.csv`);
          } catch (e) {
            console.log(e);
          } finally {
            setLoading(false);
          }
        },
        (err) => {
          setLoading(false);
          toast.error('Algo de errado aconteceu, tente exportar novamente.');
          console.log(err);
        }
      )
    );
  }, []);

  const [exportDetailed] = useCallbackObservable(() => {
    setOpen(false);
    setLoading(true);
    return salesService.deitaledExport().pipe(
      tap(
        (data) => {
          try {
            const dateExport = moment(new Date()).format('DD-MM-YYYY');
            let fileExport = new Blob([data as any], { type: 'text/csv; charset=utf-8' });
            saveAs(fileExport, `Detalhado - minhas-vendas-${dateExport}.csv`);
          } catch (e) {
            console.log(e);
          } finally {
            setLoading(false);
          }
        },
        (err) => {
          setLoading(false);
          toast.error('Algo de errado aconteceu, tente exportar novamente.');
          console.log(err);
        }
      )
    );
  }, []);

  const handleToggle = () => {
    setOpen((prevOpen: boolean) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleClickSimple = useCallback(async () => {
    if (totalItems > EXPORT_MAX_LINES_SIZE) {
      await Alert.show({
        title: EXPORT_MAX_LINES_TITLE,
        message: EXPORT_MAX_LIZES_MESSAGE,
      });
      return;
    }

    if (totalItems > 3000) {
      setExportType(false);
      setOpen(false);
      setModalOpen(true);
      return;
    } else {
      setModalInfo(true);
    }
    exportSimple();
  }, [exportSimple, totalItems]);

  const handleClickDetailed = useCallback(async () => {
    if (totalItems > EXPORT_MAX_LINES_SIZE) {
      await Alert.show({
        title: EXPORT_MAX_LINES_TITLE,
        message: EXPORT_MAX_LIZES_MESSAGE,
      });
      return;
    }

    if (totalItems > 3000) {
      setExportType(true);
      setOpen(false);
      setModalOpen(true);
      return;
    } else {
      setModalInfo(true);
    }
    exportDetailed();
  }, [exportDetailed, totalItems]);

  const onCloseBackgroundExport = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const onCloseModalInfo = useCallback(() => {
    setModalInfo(false);
  }, [setModalInfo]);

  const onOKDetailed = useCallback((email, callback) => salesService.exportBackground(email, true).pipe(tap(callback)), []);
  const onOKSimple = useCallback((email, callback) => salesService.exportBackground(email, false).pipe(tap(callback)), []);

  return (
    <div>
      <PermissionHide action='myeduzz:minhas-vendas:exportar'>
        <ButtonGroup color='primary' ref={anchorRef} aria-label='split button' id='export-sales-button'>
          <ButtonHouston
            className={classes.exportBtn}
            disabled={!totalItems}
            onClick={handleToggle}
            variant='text'
            loading={loading}
            startIcon={<SystemUpdateAltIcon />}
          >
            Exportar
          </ButtonHouston>
        </ButtonGroup>
      </PermissionHide>
      <Popper className={classes.popper} open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu'>
                  <MenuItem id='sales-button-export-simple' onClick={handleClickSimple}>
                    Simples
                  </MenuItem>
                  <MenuItem id='sales-button-export-detailed' onClick={handleClickDetailed}>
                    Detalhado
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      <BackgroundExportModal
        reportDescription='minhas vendas'
        open={modalOpen}
        onClose={onCloseBackgroundExport}
        onOk={exportType ? onOKDetailed : onOKSimple}
      />
      <ModalInfo open={modalInfo} onClose={onCloseModalInfo} />
    </div>
  );
};
export default Export;
